import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Building2, PlusCircle, Pencil, Trash2 } from 'lucide-react';

const ENTITY_TYPE_COLORS = {
  'Investment Company': 'bg-blue-500', 'Holding Company': 'bg-purple-500',
  'Property Company': 'bg-green-500', 'Family Office SPV': 'bg-amber-500'
};

const formatCurrency = (value) => {
  if (!value && value !== 0) return '$0.00';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(value);
};

const EntityCardGrid = ({ entities, entityInvestments, isReadOnly, handleReadOnlyAction, onEdit, onDelete, onAdd }) => {
  if (entities.length === 0) {
    return (
      <Card className="border-slate-200" data-testid="entity-empty-state">
        <CardContent className="py-8 px-4 sm:px-8">
          <div className="flex flex-col items-center text-center max-w-2xl mx-auto">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6 w-full">
              <div className="text-left bg-slate-50 rounded-lg p-3 border border-slate-200">
                <div className="text-xs font-semibold text-slate-700 mb-1">Examples</div>
                <div className="text-xs text-slate-600">Family LLC · Property SPV · Investment company · Family office</div>
              </div>
              <div className="text-left bg-slate-50 rounded-lg p-3 border border-slate-200">
                <div className="text-xs font-semibold text-slate-700 mb-1">Why add one?</div>
                <div className="text-xs text-slate-600">Track <span className="font-medium">indirect</span> holdings separately from personal ones — useful for tax, succession & reporting.</div>
              </div>
              <div className="text-left bg-slate-50 rounded-lg p-3 border border-slate-200">
                <div className="text-xs font-semibold text-slate-700 mb-1">What changes?</div>
                <div className="text-xs text-slate-600">Investments you mark as <span className="font-medium">Indirect</span> can be linked to an entity and rolled up on the Overview tab.</div>
              </div>
            </div>
            <Button
              onClick={() => isReadOnly ? handleReadOnlyAction() : onAdd()}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="entity-empty-add-btn"
            >
              <PlusCircle className="w-4 h-4 mr-2" /> Add Your First Entity
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {entities.map((entity) => {
        const inv = entityInvestments[entity.entity_id] || { count: 0, value: 0 };
        return (
          <Card key={entity.entity_id} className="hover:shadow-lg transition-shadow">
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <CardTitle className="text-lg font-semibold text-slate-900 mb-1">{entity.name}</CardTitle>
                  <Badge className={`${ENTITY_TYPE_COLORS[entity.type] || 'bg-slate-500'} text-white text-xs`}>{entity.type}</Badge>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="text-sm text-slate-600"><span className="font-medium">Country:</span> {entity.country}</div>
              <div className="flex items-center justify-between pt-2 border-t">
                <div>
                  <div className="text-xl sm:text-2xl font-bold text-emerald-600">{formatCurrency(inv.value)}</div>
                  <div className="text-xs text-slate-500">{inv.count} investment{inv.count !== 1 ? 's' : ''}</div>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="ghost" onClick={() => isReadOnly ? handleReadOnlyAction() : onEdit(entity)}>
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => onDelete(entity.entity_id)} className="text-red-600 hover:text-red-700 hover:bg-red-50">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              {entity.notes && <div className="text-xs text-slate-500 italic pt-2 border-t">{entity.notes}</div>}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};

export default EntityCardGrid;
